import {
  generatedApi,
  useEventControllerCatalogQuery,
  useEventControllerListQuery,
  useEventControllerStatsQuery,
  useWebhookControllerCreateMutation,
  useWebhookControllerDeliveriesQuery,
  useWebhookControllerListQuery,
  useWebhookControllerRemoveMutation,
  useWebhookControllerResendMutation,
  useWebhookControllerRotateMutation,
  useWebhookControllerTestMutation,
  useWebhookControllerUpdateMutation,
} from '@/services/generated/api';
import type {
  CreateWebhookSubscriptionDto,
  UpdateWebhookSubscriptionDto,
  WebhookSubscriptionDto,
  WebhookSubscriptionWithSecretDto,
  WebhookSecretRotatedDto,
  WebhookTestQueuedDto,
  WebhookDeletedDto,
  WebhookDeliveryDto,
  WebhookResendQueuedDto,
  EventCatalogResponseDto,
  EventCountByNameDto,
  DomainEventResponseDto,
  EventListResponseDto,
} from '@/services/generated/api';

/**
 * The webhooks layer over the client generated from OpenAPI (view 32, §4.10 of
 * the screen inventory). The same pattern as `features/dtes/dtesApi.ts`: it only
 * puts domain names on the generated hooks, no hand-written types.
 *
 * `GET /api/webhooks/subscriptions` neither paginates nor filters
 * (`WebhookControllerListApiArg` is `void`): it is a complete list, so
 * `WebhooksPage` needs neither `CursorPagination` nor `FilterBar`.
 */
export const webhooksApi = generatedApi;

export const useListWebhookSubscriptionsQuery = useWebhookControllerListQuery;
export const useCreateWebhookSubscriptionMutation = useWebhookControllerCreateMutation;
export const useUpdateWebhookSubscriptionMutation = useWebhookControllerUpdateMutation;
export const useRemoveWebhookSubscriptionMutation = useWebhookControllerRemoveMutation;
export const useRotateWebhookSecretMutation = useWebhookControllerRotateMutation;
export const useTestWebhookSubscriptionMutation = useWebhookControllerTestMutation;

/** `GET /api/events/catalog` (D of the brief: populate `EventMultiSelect` from
 *  here, never from a hardcoded list). */
export const useEventCatalogQuery = useEventControllerCatalogQuery;

/**
 * Vista 33 (§4.10 / §7.2): `GET /api/webhooks/deliveries?subscriptionId&status`
 * and `POST /api/webhooks/deliveries/:id/resend`, the manual way out of the DLQ.
 */
export const useListWebhookDeliveriesQuery = useWebhookControllerDeliveriesQuery;
export const useResendWebhookDeliveryMutation = useWebhookControllerResendMutation;

/**
 * Vista 34 (§4.10 / §7.2): `GET /api/events?name&aggregateId&cursor&limit` y
 * `GET /api/events/stats`. The catalogue (`useEventCatalogQuery`, above) is
 * reused for the name filter — a single source of truth, just as in view 32.
 *
 */
export const useEventListQuery = useEventControllerListQuery;
export const useEventStatsQuery = useEventControllerStatsQuery;

export type WebhookSubscription = WebhookSubscriptionDto;
export type WebhookSubscriptionWithSecret = WebhookSubscriptionWithSecretDto;
export type WebhookSecretRotated = WebhookSecretRotatedDto;
export type WebhookTestQueued = WebhookTestQueuedDto;
export type WebhookDeleted = WebhookDeletedDto;
export type CreateWebhookSubscription = CreateWebhookSubscriptionDto;
export type UpdateWebhookSubscription = UpdateWebhookSubscriptionDto;
export type EventCatalog = EventCatalogResponseDto;

/** A domain event's name, exactly as the catalogue brings it. */
export type DomainEventName = EventCatalogResponseDto['events'][number];

export type WebhookDelivery = WebhookDeliveryDto;
export type WebhookDeliveryStatus = WebhookDeliveryDto['status'];
export type WebhookResendQueued = WebhookResendQueuedDto;

export type DomainEvent = DomainEventResponseDto;
export type EventListPage = EventListResponseDto;
export type EventCountByName = EventCountByNameDto;
